// Presentation-only formatters: how a value is spelled, never what it is.
//
// Small on purpose. Each of these exists because two or more pages name the same
// thing and would otherwise drift: a grade level whose internal scale puts K at 0,
// a month label, a UTC bucket boundary, and a query string that has to be added to
// rather than replaced.
//
// No React. The one import is tokens.js, itself a leaf with no imports of its
// own -- the grade-relative helpers below spell a value using names that live
// with the palette, and a second copy of those names is exactly what this file
// exists to prevent.
import { LEVEL_REL, LEVEL_REL_ORDER, LEVEL_OFF_STEPS } from "./tokens";

// Internal grade scale: K is 0 and PreK/below is -1 (see clean_grade_values
// server-side), so a bare String(g) mislabels both ends. Lives here because the
// curriculum tab and the Overview's gap card both name grade levels, and two
// copies of this convention is one too many.
const gradeLabel = (g) => (g === 0 ? "K" : g <= -1 ? "PreK" : String(g));

const fmtMonth = (iso) => {
  const d = new Date(iso);
  return d.toLocaleDateString(undefined, { month: "short", year: "2-digit" });
};

// Merge one key into a query string, returning a fresh "?a=b&c=d" (or "" when
// nothing is left). The dashboard's links have to ADD their own param on top of
// whatever scope state is already in the URL rather than replacing it: the
// Overview's digest cards deep-link a specific lens, and the legacy /schools and
// /engagement routes redirect onto one — all of which must still carry
// ?organization= and ?start= through the hop.
const withParam = (search, key, value) => {
  const qp = new URLSearchParams(search || "");
  if (value === null || value === undefined || value === "") qp.delete(key);
  else qp.set(key, value);
  const qs = qp.toString();
  return qs ? `?${qs}` : "";
};

// Bucket boundaries are UTC midnights ("...T00:00:00+00:00"); format them in
// UTC or the label shifts back a month/day in any western timezone.
const fmtBucketLabel = (iso, interval) =>
  new Date(iso).toLocaleDateString(
    undefined,
    interval === "week"
      ? { month: "short", day: "numeric", timeZone: "UTC" }
      : { month: "short", year: "2-digit", timeZone: "UTC" }
  );

// How to name the trailing recency window the "right now" reads are scored over
// (RECENT_WINDOW_DAYS on the backend). It is clamped to the window start, so
// early in a school year it is shorter than four weeks and must not be labelled
// as four weeks -- a district three days into its year would be told its whole
// month produced two lessons.
//
// One function so the mastery card and the fleet card word it identically; they
// are two facts about the same span sitting 200px apart.
const fmtRecentSpan = (days) => {
  // A payload that predates the block carries no span. Only reachable for the 15
  // minutes a cached response outlives a deploy, but "last 4 weeks" over a number
  // that was read off a bucket is the one thing this function exists to prevent.
  if (days == null) return "recent period";
  if (days >= 28) return "last 4 weeks";
  if (days >= 14) return `last ${Math.round(days / 7)} weeks`;
  return `last ${days} days`;
};

// The key a row's NAME sorts on. People sort by surname; everything else sorts
// by the string as printed.
//
// The key comes from the SERVER (`sortName`, teacher rows only), because deriving
// it here means guessing where the given name ends and every available rule is
// wrong on real rosters: split at the first space and "Billie Jo Dial" files
// under J; split at the last and "Mary Van Der Berg" files under B. The server
// has `first_name` and `last_name` as separate columns and does not have to guess
// — see LearnerSegments.teacher_sort_names.
//
// Everything else falls through to the printed name, which is what places and
// grade labels want: applying a surname rule blind would file "Gordon Elementary
// School" under S and "Grade 3" under 3. "No teacher on record" lands here too —
// it is a bucket rather than a person, and the server sends it no key.
const nameSortKey = (row) =>
  ((row && row.sortName) || (row && row.name) || "").toLowerCase();

// Grade rows sort by grade LEVEL, not by label. "Sort by grade" means PreK, K,
// 1, 2 to everyone who reads a roster, and alphabetically it means Grade 1,
// Grade 2, Grade 3, Kindergarten, PreK — a sequence with no meaning at all.
//
// The number comes from the segment id (`grade:0` is K, `grade:-1` is PreK; see
// grade_bucket server-side) rather than from the label, because the label is
// exactly the thing that doesn't sort. Non-numeric buckets — `grade:None`, and
// any raw value grade_bucket passed through untouched — return null: they have no
// place on the scale, and callers pin them rather than giving them one.
const GRADE_SEGMENT = "grade:";
const gradeOrdinal = (row) => {
  const id = String((row && row.id) || "");
  if (!id.startsWith(GRADE_SEGMENT)) return null;
  const n = Number(id.slice(GRADE_SEGMENT.length));
  return Number.isFinite(n) ? n : null;
};
// A grade row, whether or not it lands on the scale. Callers need both facts
// separately: this one decides how the ORDER is described ("lowest first", not
// "A–Z"), gradeOrdinal decides where a row goes.
const isGradeRow = (row) =>
  String((row && row.id) || "").startsWith(GRADE_SEGMENT);
// The unknown bucket, which is a roster-hygiene finding rather than a grade.
const isUnplacedGrade = (row) => isGradeRow(row) && gradeOrdinal(row) === null;

// The comparator behind every name/label sort in the dashboard: grade levels
// numerically, people by surname, everything else by the string as printed.
//
// Unplaced grade rows compare EQUAL here and are pinned by the caller, so that
// the pin survives a descending click — see PINNED_LAST in FocusTab.
const byName = (a, b) => {
  const ga = gradeOrdinal(a);
  const gb = gradeOrdinal(b);
  if (ga !== null && gb !== null) return ga - gb;
  if (isGradeRow(a) && isGradeRow(b)) return 0;
  return nameSortKey(a).localeCompare(nameSortKey(b), undefined, {
    sensitivity: "base",
  });
};

// -- grade-relative content level ------------------------------------------ #
//
// A row's `by_level` (mastery events split by where the content sat relative to
// the grade of the learner who worked it) reduced to something sayable. Shared
// by the curriculum list and the units x schools matrix, which say it at two
// different lengths from the same numbers.

const relTotal = (byLevel) =>
  !byLevel
    ? 0
    : [...LEVEL_REL_ORDER, "unknown"].reduce(
        (a, k) => a + (byLevel[k] || 0), 0);

// The single direction a row was worked at, or null when it genuinely spans
// more than one. Not collapsed to a winner: on an all-grades roster the same
// grade-1 unit really is on grade for the grade-1 learners and remediation for
// the older ones, and naming one would misdescribe the reader's own district.
const relSummary = (byLevel) => {
  if (!byLevel) return null;
  const present = LEVEL_REL_ORDER.filter((k) => (byLevel[k] || 0) > 0);
  const unknown = byLevel.unknown || 0;
  if (!present.length) return unknown ? LEVEL_REL.unknown : null;
  if (present.length === 1 && !unknown) return LEVEL_REL[present[0]];
  return null;
};

// The whole split spelled out, for a chip's or a row's title.
const relBreakdown = (byLevel) => {
  const total = relTotal(byLevel);
  if (!total) return "";
  return [...LEVEL_REL_ORDER, "unknown"]
    .filter((k) => (byLevel[k] || 0) > 0)
    .map((k) => `${Math.round((100 * byLevel[k]) / total)}% ${LEVEL_REL[k].long}`)
    .join(" · ");
};

// Direction alone, for somewhere too narrow for the word (the matrix's sticky
// unit column). Empty for on-grade, unknown, and mixed -- an arrow that could
// mean either direction is worse than no arrow.
const relGlyph = (byLevel) => {
  const rel = relSummary(byLevel);
  return rel ? rel.glyph : "";
};

// The share of a row's work that sat off grade, in either direction -- the
// magnitude the chip's surface weight encodes. `unknown` is not off-grade, so it
// stays out of the numerator while remaining in the denominator: a row we mostly
// cannot place should not read as a row that is mostly off grade.
const relOffShare = (byLevel) => {
  const total = relTotal(byLevel);
  if (!total) return 0;
  return ((byLevel.below || 0) + (byLevel.above || 0)) / total;
};

// The chip's surface + ink. One rule covers pure and mixed rows alike: a row
// worked entirely below grade is simply the top of the same ramp.
const relTone = (byLevel) => {
  const off = relOffShare(byLevel);
  return LEVEL_OFF_STEPS.find((s) => off >= s.min) || LEVEL_OFF_STEPS[LEVEL_OFF_STEPS.length - 1];
};

// What a row's chip says. Pure rows name their direction; mixed rows -- which on
// a real all-grades district roster is the great majority of them (82 of 90 for
// our highest-volume district) -- get the share that was ON grade rather than
// the word "mixed". "Mixed" is true of almost every row and therefore tells a
// scanning reader nothing; a proportion is just as honest and is the number the
// on-grade headline is made of.
const relChipText = (byLevel) => {
  const rel = relSummary(byLevel);
  if (rel) return rel === LEVEL_REL.unknown ? "" : rel.label;
  const total = relTotal(byLevel);
  if (!total) return "";
  return `${Math.round((100 * (byLevel.on || 0)) / total)}% on grade`;
};

export {
  gradeLabel,
  relSummary,
  relBreakdown,
  relGlyph,
  relChipText,
  relOffShare,
  relTone,
  fmtMonth,
  withParam,
  fmtBucketLabel,
  fmtRecentSpan,
  byName,
  isGradeRow,
  isUnplacedGrade,
};
